import PropTypes from "prop-types";
import { memo } from "react";
import Merchandising from "app/pages/Listing/merchandising/Merchandising";
import { RESOLUTION } from "app/constants";
import { resolutionPropType } from "app/utils/propTypes";
import "./zoom-merchandising-carousel.scss";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import Swiper from "react-id-swiper";
import SimpleBackgroundProgressiveImg from "app/pages/.shared/SimpleBackgroundProgressiveImg";
import SimpleProgressiveImg from "app/pages/.shared/SimpleProgressiveImg";
import Loader from "app/pages/.shared/Loader";

const IMG_SIZES_LARGE = {
	large: { width: 1024, height: 415 },
	xlarge: { width: 1024, height: 415 },
	medium: { width: 1024, height: 415 },
};

const IMG_SIZES_SMALL = {
	small: { width: 768, height: 834 },
};

const CLOUDINARY_OPTIONS = ["c_crop"];

const spaceBetween = {
	[RESOLUTION.XLARGE]: 22,
	[RESOLUTION.LARGE]: 22,
	[RESOLUTION.MEDIUM]: 15,
	[RESOLUTION.SMALL]: 15,
};

// @see https://github.com/kidjp85/react-id-swiper/issues/336
const ensureSlidesAreLazyLoaded = swiperInstance => {
	const loadPreviousSlides = () => {
		const { slidesPerView } = swiperInstance.params;
		const { activeIndex } = swiperInstance;

		const loadPreviousTotal = Math.ceil((slidesPerView !== "auto" ? slidesPerView : 2) / 2);
		for (let i = 0; i <= loadPreviousTotal; i++) {
			swiperInstance.lazy.loadInSlide(activeIndex - i);
		}
	};

	if (swiperInstance !== null) {
		loadPreviousSlides();
		swiperInstance.on("slideChange", loadPreviousSlides);
	}
};

const swiperParams = {
	getSwiper: ensureSlidesAreLazyLoaded,
	speed: 500,
	slidesPerView: 1.18,
	roundLengths: true, // pour que le texte sur les images ne soient pas floues
	centeredSlides: true,
	loop: true,
	rebuildOnUpdate: true,
	loopedSlides: 3,
	containerClass: "swiper-container zoom-merchandising-carousel",
	navigation: {
		nextEl: ".zoom-merchandising-carousel__button--next",
		prevEl: ".zoom-merchandising-carousel__button--previous",
	},
	// eslint-disable-next-line react/no-multi-comp
	renderPrevButton: () => (
		<div className="zoom-merchandising-carousel__button zoom-merchandising-carousel__button--previous">
			<i className="icon icon--previous" />
		</div>
	),
	// eslint-disable-next-line react/no-multi-comp
	renderNextButton: () => (
		<div className="zoom-merchandising-carousel__button zoom-merchandising-carousel__button--next">
			<i className="icon icon--next" />
		</div>
	),
};

// eslint-disable-next-line react/no-multi-comp
const ZoomMerchandisingCarousel = ({ merchandisingList, resolution }) => {
	swiperParams.spaceBetween = spaceBetween[resolution];

	const carouselContainerStyle = {
		minHeight: "auto",
		height: "auto",
	};

	if (resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE) {
		carouselContainerStyle.height = "350px";
	} else if (resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL) {
		carouselContainerStyle.minHeight = "264px";
	}

	const isServerSide = typeof window === "undefined";

	// le premier rendu du carousel n'est pas satisfaisant. On préfère ne rien rendre
	return isServerSide ? (
		<div
			style={{
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				...carouselContainerStyle,
			}}
		>
			<Loader />
		</div>
	) : (
		<div style={carouselContainerStyle}>
			<Swiper {...swiperParams}>
				{merchandisingList.map(merchandising => {
					let imageElement = false;

					if (resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE) {
						imageElement = (
							<SimpleBackgroundProgressiveImg
								resolution={resolution}
								sizes={IMG_SIZES_LARGE}
								cloudinaryOptions={CLOUDINARY_OPTIONS}
								src={get(merchandising, "photoLarge.url")}
								backgroundPosition="center"
							/>
						);
					} else if (resolution === RESOLUTION.MEDIUM) {
						imageElement = (
							<SimpleProgressiveImg
								resolution={resolution}
								sizes={IMG_SIZES_LARGE}
								cloudinaryOptions={CLOUDINARY_OPTIONS}
								src={get(merchandising, "photoLarge.url")}
							/>
						);
					} else if (resolution === RESOLUTION.SMALL) {
						imageElement = (
							<SimpleProgressiveImg
								sizes={IMG_SIZES_SMALL}
								cloudinaryOptions={CLOUDINARY_OPTIONS}
								src={get(merchandising, "photoSmall.url")}
							/>
						);
					}

					// En preview, le back nous renvoie les merch de même code. Coté front on veut pouvoir afficher les 2
					const merchKey = `${merchandising.code}-${merchandising.startAt}`;

					return (
						<div key={merchKey}>
							<Merchandising
								imageElement={imageElement}
								ctaButtonText={get(merchandising, "button.label")}
								ctaButtonBackgroundColor={get(
									merchandising,
									"button.backgroundColor"
								)}
								ctaButtonColor={get(merchandising, "button.color")}
								merchandisingCode={get(merchandising, "code")}
								position={get(merchandising, "position")}
								indexCarousel={merchandising.headerIndex}
								endAt={merchandising.endAt}
								countdownPosition={get(merchandising, "countdown.position")}
								countdownTextColor={get(merchandising, "countdown.textColor")}
								countdownStartAt={get(merchandising, "countdown.startAt")}
								countdownEndAt={get(merchandising, "countdown.endAt")}
								daysInHours={get(merchandising, "countdown.daysInHours")}
								redirectUrl={get(merchandising, "redirectUrl")}
							/>
						</div>
					);
				})}
			</Swiper>
		</div>
	);
};

ZoomMerchandisingCarousel.defaultProps = {
	merchandisingList: [],
};

ZoomMerchandisingCarousel.propTypes = {
	merchandisingList: PropTypes.arrayOf(PropTypes.object),
	resolution: resolutionPropType,
};

export default memo(ZoomMerchandisingCarousel, (prevProps, nextProps) => {
	return (
		isEqual(prevProps.merchandisingList, nextProps.merchandisingList) &&
		prevProps.resolution === nextProps.resolution
	);
});
